import { type NetworkQualityLevel } from 'twilio-video'
import { logMessage } from '../../../Logging/sentry'
import { QualityStats, TrackKey } from '../../webRTCRoom'

/**
 * Extract info contained in participant identity
 * Eg: producer|1598873177430|Casper Storm|7484220 => 7484220.
 */
const extractInfoFromIdentity = (identity: string) => {
  const [role, joinedAt, name, id] = identity.split('|')

  if (role !== 'producer' && role !== 'presenter' && role !== 'headless') {
    logMessage('Detected participant with invalid role.', {
      level: 'error',
      extra: {
        identity,
        role,
        joinedAt,
        name,
      },
    })
  }

  return {
    role: role as 'producer' | 'presenter' | 'headless',
    joinedAt: +joinedAt,
    name,
    id: +id,
  }
}

const extractRoleFromIdentity = (identity: string) => {
  return extractInfoFromIdentity(identity).role
}

const extractIdFromIdentity = (identity: string) => {
  return extractInfoFromIdentity(identity).id || 0
}

const extractJoinedAtFromIdentity = (identity: string) => {
  return extractInfoFromIdentity(identity).joinedAt
}

const extractNameFromIdentity = (identity: string) => {
  return extractInfoFromIdentity(identity).name
}

/** Function returns the normalized track name. */
const getNormalizedTrackName = (trackName: string): TrackKey | 'unknown' => {
  if (trackName.includes('cameraAudio')) return 'cameraAudio'
  if (trackName.includes('screenAudio')) return 'screenAudio'
  if (trackName.includes('camera')) return 'camera'
  if (trackName.includes('screen')) return 'screen'
  if (trackName.includes('mobile')) return 'mobile'

  return 'unknown'
}

const translateNetworkQualityLevel = (
  networkQualityLevel: NetworkQualityLevel | null
): QualityStats => {
  if (networkQualityLevel === null || networkQualityLevel < 2) {
    return 'bad'
  }
  if (networkQualityLevel > 3) {
    return 'excellent'
  }
  return 'acceptable'
}

export {
  extractInfoFromIdentity,
  extractRoleFromIdentity,
  extractIdFromIdentity,
  extractJoinedAtFromIdentity,
  extractNameFromIdentity,
  getNormalizedTrackName,
  translateNetworkQualityLevel,
}
